import React, { useEffect, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, BarElement, Title, Tooltip, Legend, ArcElement,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";
import Handler from "../Helpers/Handler";
import moment from "moment";
import AOS from "aos";

ChartJS.register( CategoryScale, LinearScale, LineElement, PointElement, BarElement, Title, Tooltip, Legend, ArcElement);

const UserDashboard = () => {
  const [data, setData] = useState();

  const getData = async () => {
    const response = await Handler({ method: "get", url: `/dashboard/user.php` });
    if (response?.success) {
      setData(response?.data);
    }
  };

  const parseTrend = (obj) => {
    if (!obj || typeof obj !== "object") return [];
    return Object.entries(obj);
  };

  useEffect(() => {
    getData();
    AOS.init({ duration: 700, offset: 30 });
  }, []);

  // Destructure safely
  const {
    profile = {},
    subscription,
    order_stats = {},
    product_stats = {},
    staff_list = [],
    pending_orders = [],
    revenue = {},
    daily_trend = {},
    monthly_trend = {},
    last_login,
  } = data || {};

  // --- Daily Trend ---
  const dailyParsed = parseTrend(daily_trend);
  const dailyLabels = dailyParsed.map((x) => x[0]);
  const dailyValues = dailyParsed.map((x) => x[1]);

  const dailyData = {
    labels: dailyLabels,
    datasets: [
      {
        label: "Daily Revenue",
        data: dailyValues,
        borderColor: "var(--primary)",
        backgroundColor: "rgba(71,47,176,0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Fallback for empty daily trend
  const emptyLineData = {
    labels: ["No Data"],
    datasets: [
      {
        label: "No Data",
        data: [0],
        borderColor: "#ccc",
        backgroundColor: "rgba(200,200,200,0.3)",
      },
    ],
  };

  // --- Monthly Trend ---
  const monthlyParsed = parseTrend(monthly_trend);
  const monthLabels = monthlyParsed.map((x) => x[0]);
  const monthValues = monthlyParsed.map((x) => x[1]);

  const monthlyData = {
    labels: monthLabels,
    datasets: [
      {
        label: "Monthly Revenue",
        data: monthValues,
        backgroundColor: "rgba(255, 88, 132, 0.9)",
      },
    ],
  };

  const emptyBarData = {
    labels: ["No Data"],
    datasets: [
      {
        label: "No Data",
        data: [0],
        backgroundColor: "rgba(200,200,200,0.5)",
      },
    ],
  };

  // --- Pie Data ---
  const paid = order_stats.paid_orders || 0;
  const pending = order_stats.pending_orders || 0;

  const pieData = {
    labels: ["Paid Orders", "Pending Orders"],
    datasets: [
      {
        data: [paid, pending],
        backgroundColor: ["#4caf50", "#ffcc00"],
      },
    ],
  };

  const emptyPieData = {
    labels: ["No Data"],
    datasets: [
      {
        data: [1],
        backgroundColor: ["#e0e0e0"],
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: "top" } },
  };

  // Dashboard Cards
  const dashboardCards = [
    { icon: "bx bx-money", label: "Daily Revenue", value: `₹${revenue.daily_revenue || 0}`, color: "blue" },
    { icon: "bx bx-calendar", label: "Monthly Revenue", value: `₹${revenue.monthly_revenue || 0}`, color: "green" },
    { icon: "bx bx-line-chart", label: "Total Revenue", value: `₹${revenue.total_revenue || 0}`, color: "orange" },
    { icon: "bx bx-user-check", label: "Staff Count", value: staff_list.length, color: "pink" },

    { icon: "bx bx-hourglass", label: "Pending Orders", value: pending_orders.length, color: "blue" },
    { icon: "bx bx-package", label: "Total Products", value: product_stats.total_products, color: "green" },
    { icon: "bx bx-check-circle", label: "Active Products", value: product_stats.active_products, color: "orange" },
    { icon: "bx bx-x-circle", label: "Inactive Products", value: product_stats.inactive_products, color: "pink" },

    { icon: "bx bx-cart", label: "Total Orders", value: order_stats.total_orders || 0, color: "blue" },
    { icon: "bx bx-check-double", label: "Paid Orders", value: paid, color: "green" },
    { icon: "bx bx-time-five", label: "Pending", value: pending, color: "orange" },
    { icon: "bx bx-rupee", label: "Total Spend", value: `₹${order_stats.total_spend || 0}`, color: "pink" },

    { icon: "bx bx-user", label: "User Name", value: profile.name, color: "blue" },
    { icon: "bx bx-mobile-alt", label: "Mobile", value: profile.mobile, color: "green" },
    { icon: "bx bx-log-in", label: "Subscription Upto", value: last_login ? moment(subscription?.end_date).format("ll") : "--", color: "orange" },
    { icon: "bx bx-check-shield", label: "Subscription", value: subscription ? subscription.status : "Inactive", color: "pink" },
  ];

  return (
    <div className="dashboard container mt-4 pb-5 animate__animated animate__fadeIn">
      <h2 className="dashboard-title mb-4 animate__fadeInDown">📊 Dashboard Overview</h2>

      {/* Subscription Banner */}
      {/* {!subscription && (
        <div className="col-12 mb-4">
          <div className="renew-banner shadow-sm">
            <div className="row align-items-center">
              <div className="col-md-1 col-2 text-center">
                <i className="bx bxs-error-circle renew-icon"></i>
              </div>
              <div className="col-md-7 col-10">
                <h5 className="renew-title">Subscription Expired</h5>
                <p className="renew-text mb-0">
                  Your subscription has expired. Renew now to continue using all features.
                </p>
              </div>
              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                <button className="btn renew-btn">
                  <i className="bx bx-reset"></i> <a className="text-white" href="tel:91950044441">Contact Now</a>
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Cards */}
      <div className="cards-grid">
        {dashboardCards.map((c, i) => (
          <div className={`info-card card-${c.color}`} key={i} data-aos="fade-up">
            <div className="icon-box">
              <i className={c.icon}></i>
            </div>
            <div className="details">
              <div className="value">{c.value}</div>
              <div className="label">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row mt-4">

        {/* Pie Chart */}
        <div className="col-md-4 mb-4">
          <div className="dashboard-card chart-card pie-chart">
            <h5 className="section-title text-center">🟢 Orders Breakdown</h5>
            <Pie data={(paid || pending) ? pieData : emptyPieData} options={chartOptions} />
          </div>
        </div>

        {/* Daily Trend */}
        <div className="col-md-4 mb-4">
          <div className="dashboard-card chart-card">
            <h5 className="section-title text-center">📈 Daily Trend</h5>
            <Line data={dailyParsed.length ? dailyData : emptyLineData} />
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="col-md-4 mb-4">
          <div className="dashboard-card chart-card">
            <h5 className="section-title text-center">📆 Monthly Trend</h5>
            <Bar data={monthlyParsed.length ? monthlyData : emptyBarData} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserDashboard;
